import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { FormattedMessage, useIntl } from "react-intl"
import classNames from "classnames"

// Components
import {
  CustomButton,
  Modal,
  Spinner,
  SelectableLabelPill
} from "../../../common"

// Services
import { createLabel } from "../../../../services/label"

// Constants
import { GA_EVENTS } from "../../../../constants/analytics"

export const DEFAULT_COLOR_PICKER_COLOR = "#002060" // Fribl-blue

const LabelsModal = ({
  open = false,
  setOpen = () => {},
  onConfirm = async (selectedLabels = []) => {},
  onCancel = () => {},
  labels = [],
  preSelectedLabels = [],
}) => {
  const { formatMessage } = useIntl();
  const [selectedLabels, setSelectedLabels] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [showCreateLabel, setShowCreateLabel] = useState(false);
  const [newLabelText, setNewLabelText] = useState("");
  
  const [newLabelColor, setNewLabelColor] = useState(DEFAULT_COLOR_PICKER_COLOR);
  const [tempCreatedLabels, setTempCreatedLabels] = useState([]);

  const resetNewLabelInputState = () => {
    setShowCreateLabel(false);
    setNewLabelText("");
    setNewLabelColor(DEFAULT_COLOR_PICKER_COLOR);
  }

  // Update selected labels when modal opens or preSelectedLabels change
  useEffect(() => {
    if (open) {
      setTempCreatedLabels([]);
      resetNewLabelInputState();
      
      if (preSelectedLabels.length > 0) {
        setSelectedLabels(new Set(preSelectedLabels.map(label => label.id)));
      } else {
        setSelectedLabels(new Set());
      }
    }
  }, [open, preSelectedLabels]);

  // Combine existing labels with temporary created labels
  const allLabels = [...labels, ...tempCreatedLabels];

  const handleLabelClick = (labelId) => {
    setSelectedLabels(prev => {
      const newSelectedLabels = new Set(prev);
      if (newSelectedLabels.has(labelId)) {
        newSelectedLabels.delete(labelId);
      } else {
        newSelectedLabels.add(labelId);
      }
      return newSelectedLabels;
    });
  };

  const handleCreateNewLabel = () => {
    setShowCreateLabel(true);
  };

  const handleNewLabelSubmit = (e) => {
    e.preventDefault();
    if (newLabelText.trim()) {
      // Create a temporary ID for the new label (using timestamp to avoid conflicts)
      const tempId = `temp-${Date.now()}`;
      const newLabel = {
        id: tempId,
        name: newLabelText.trim(),
        color: newLabelColor,
        isTemp: true // Flag to identify temporary labels
      };
      
      // Add the new label to temporary labels
      setTempCreatedLabels(prev => [...prev, newLabel]);
      
      // Automatically select the newly created label
      setSelectedLabels(prev => new Set([...prev, tempId]));
      
      resetNewLabelInputState();
    }
  };

  const handleNewLabelCancel = () => {
    resetNewLabelInputState();
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      // First, create any temporary labels via API
      const createdLabelsMap = new Map(); // Maps temp ID to real ID
      let createdLabelsCount = 0;
      
      for (const selectedLabelId of selectedLabels) {
        const tempLabel = tempCreatedLabels.find(label => label.id === selectedLabelId);
        if (tempLabel && tempLabel.isTemp) {
          // Create the label via API
          const createdLabel = await createLabel(tempLabel.name, tempLabel.color);
          createdLabelsMap.set(tempLabel.id, createdLabel.id);
          createdLabelsCount++;
        }
      }
      
      // Convert selected labels, replacing temp IDs with real IDs
      const finalSelectedLabels = Array.from(selectedLabels).map(labelId => {
        return createdLabelsMap.has(labelId) ? createdLabelsMap.get(labelId) : labelId;
      });
      
      await onConfirm(finalSelectedLabels);
      
      // Show appropriate success message
      if (createdLabelsCount > 0) {
        toast.success(formatMessage(
          { id: "toast.labelsCreatedAndAssigned" },
          { count: createdLabelsCount }
        ) || `${createdLabelsCount} new label(s) created and assigned successfully`);
      } else {
        toast.success(formatMessage({ id: "toast.labelsUpdatedSuccessfully" }));
      }
      
      setOpen(false); // Close modal after operation
    } catch (error) {
      console.error("Error in handleConfirm:", error);
      if (error.message && error.message.includes('createLabel')) {
        toast.error(formatMessage({ id: "toast.failedToCreateLabel" }) || "Failed to create new label");
      } else {
        toast.error(formatMessage({ id: "toast.failedToUpdateLabels" }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetNewLabelInputState();
    onCancel();
  };

  return (
    <Modal
      closeOnOutsideClick
      open={open}
      setOpen={setOpen}
    >
      <div className="labels-modal">
        <h2 className="labels-modal__title">
          <FormattedMessage id="modal.labels.title" />
        </h2>
        <div className="labels-modal__content">
          <p className="labels-modal__subtitle">
            <FormattedMessage id="modal.labels.subtitle" />
          </p>
          <div className="selectable-labels-container">
            {allLabels.map((label) => (
              <SelectableLabelPill 
                key={label.id} 
                label={label} 
                selected={selectedLabels.has(label.id)}
                onClick={handleLabelClick}
              />
            ))}
            
            {/* Create new label option */}
            {!showCreateLabel ? (
              <div 
                className="create-new-label" 
                onClick={handleCreateNewLabel}
              >
                + <FormattedMessage id="common.createNewLabel" defaultMessage="Create New Label" />
              </div>
            ) : (
              <form onSubmit={handleNewLabelSubmit} className="create-label-form">
                <input
                  type="text"
                  value={newLabelText}
                  onChange={(e) => setNewLabelText(e.target.value)}
                  placeholder={formatMessage({ id: "placeholder.labelName", defaultMessage: "New Label Name..." })}
                  autoFocus
                />
                <input
                  type="color"
                  value={newLabelColor}
                  onChange={(e) => setNewLabelColor(e.target.value)}
                  className="color-picker"
                  title={formatMessage({ id: "tooltip.selectColor", defaultMessage: "Select Color" })}
                />
                <button 
                  type="submit"
                  disabled={!newLabelText.trim()}
                >
                  ✓
                </button>
                <button
                  type="button"
                  onClick={handleNewLabelCancel}
                >
                  ✕
                </button>
              </form>
            )}
          </div>
        </div>
        <div className="flex labels-modal__buttons">
          <CustomButton
            className={classNames("btn btn--small primary mr-2", {
              disabled: loading || showCreateLabel,
            })}
            onClick={handleConfirm}
            disabled={loading || showCreateLabel}
            trackingEvent={GA_EVENTS.SELECT_CV.MODAL_CONFIRM}
          >
            {loading ? (
              <>
                <Spinner className="small" />
                <FormattedMessage id="common.loading" />
              </>
            ) : (
              <FormattedMessage id="common.confirm" />
            )}
          </CustomButton>
          <CustomButton
            className="btn btn--small quinary"
            onClick={handleCancel}
            disabled={loading}
            trackingEvent={GA_EVENTS.SELECT_CV.MODAL_CANCEL}
          >
            <FormattedMessage id="common.cancel" />
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default LabelsModal;
